/**
 * Turning something a person typed into something safe to put in an `href`.
 *
 * Its own module, with no React or MUI in it, for the reason `oauthRedirect.ts`
 * is: the rule is three lines, and three lines living inside a renderer is a
 * rule nobody tests and nobody notices is missing. Six components render social
 * links straight into an anchor; they now all read from here.
 */

/** The only two schemes a link out of the app may use. */
const ALLOWED_SCHEMES = new Set(['http:', 'https:']);

/**
 * Does this already declare a scheme? Used to decide whether the https retry
 * below is even appropriate — retrying something that already had one produces
 * nonsense rather than a refusal (`https://` becomes `https://https//`, a URL
 * whose host is the word "https").
 */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Resolve a stored link to an absolute `http(s)` URL, or null if it can't be.
 *
 * Two failures this exists to stop, both live on operator-entered data that
 * nothing in the write path validates:
 *
 * A scheme-less `instagram.com/theband` is not an absolute URL, so a browser
 * resolves it against the current document — from `live.showliner.io/room` the
 * fan lands on `live.showliner.io/instagram.com/theband`, which matches no
 * route, and since these links open with `target="_blank"` they get a dead
 * Showliner tab instead of the band. A bare host is the overwhelmingly likely
 * paste, so retry it under https rather than refusing what an address bar would
 * have accepted.
 *
 * And `javascript:` parses perfectly well as a URL. An allowlist rather than a
 * blocklist means a scheme nobody thought of is refused by default.
 *
 * Mirrors `services/announcementPolicy.ts` `validateLink`, which solved the same
 * problem one feature over; that one returns a reason because an operator is
 * typing it, this one returns null because a fan is reading it.
 */
export function toExternalUrl(raw: string | null | undefined): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Only a value with NO scheme gets the benefit of the doubt. Retrying one
    // that has a scheme and still failed to parse would invent a host out of
    // the scheme itself and hand back a link to nowhere.
    if (HAS_SCHEME.test(trimmed)) return null;
    try {
      parsed = new URL(`https://${trimmed}`);
    } catch {
      return null;
    }
  }

  if (!ALLOWED_SCHEMES.has(parsed.protocol)) return null;
  if (!parsed.hostname) return null;
  return parsed.toString();
}
